import { create } from 'zustand';
import type {
  Group,
  GroupMember,
  GroupPost,
  GroupInvite,
  InviteInfo,
  GroupSortType,
} from '@/types';
import * as groupsApi from '@/api/groups';

type GroupTab = 'myGroups' | 'browse' | 'invites';

interface GroupState {
  // List view
  activeTab: GroupTab;
  myGroups: Group[];
  browseGroups: Group[];
  invites: Group[];
  searchQuery: string;
  searchSort: GroupSortType;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  nextCursor: string | null;
  error: string | null;

  // Detail view
  currentGroup: Group | null;
  posts: GroupPost[];
  pinnedPosts: GroupPost[];
  postsNextCursor: string | null;
  postsHasMore: boolean;
  isLoadingDetail: boolean;
  isLoadingPosts: boolean;
  isLoadingMorePosts: boolean;

  // Members
  members: GroupMember[];
  membersNextCursor: string | null;
  membersHasMore: boolean;
  isLoadingMembers: boolean;
  isLoadingMoreMembers: boolean;

  // Invites management
  groupInvites: GroupInvite[];
  isLoadingInvites: boolean;

  // Invite resolution
  inviteInfo: InviteInfo | null;

  // Actions - List
  setActiveTab: (tab: GroupTab) => void;
  loadMyGroups: () => Promise<void>;
  loadBrowseGroups: (query?: string, sort?: GroupSortType) => Promise<void>;
  loadMyInvites: () => Promise<void>;
  loadMore: () => Promise<void>;
  setSearchQuery: (query: string) => void;
  setSearchSort: (sort: GroupSortType) => void;

  // Actions - CRUD
  createGroup: (params: {
    name: string;
    description?: string;
    hashtags?: string[];
    color?: string;
    visibility?: string;
  }) => Promise<Group>;
  updateGroup: (id: string, params: {
    name?: string;
    description?: string;
    hashtags?: string[];
    color?: string;
    visibility?: string;
  }) => Promise<Group>;
  deleteGroup: (id: string) => Promise<void>;

  // Actions - Detail
  loadGroupDetail: (id: string) => Promise<void>;
  loadGroupFeed: (groupId: string) => Promise<void>;
  loadMorePosts: (groupId: string) => Promise<void>;
  joinGroup: (id: string) => Promise<void>;
  leaveGroup: (id: string) => Promise<void>;
  deletePost: (groupId: string, postId: string) => Promise<void>;
  pinPost: (groupId: string, postId: string) => Promise<void>;
  unpinPost: (groupId: string, postId: string) => Promise<void>;

  // Actions - Members
  loadMembers: (groupId: string) => Promise<void>;
  loadMoreMembers: (groupId: string) => Promise<void>;
  approveMember: (groupId: string, userId: string) => Promise<void>;
  rejectMember: (groupId: string, userId: string) => Promise<void>;
  removeMember: (groupId: string, userId: string) => Promise<void>;
  banMember: (groupId: string, userId: string) => Promise<void>;
  unbanMember: (groupId: string, userId: string) => Promise<void>;
  muteMember: (groupId: string, userId: string) => Promise<void>;
  unmuteMember: (groupId: string, userId: string) => Promise<void>;
  updateMemberRole: (groupId: string, userId: string, role: string) => Promise<void>;
  transferOwnership: (groupId: string, userId: string) => Promise<void>;

  // Actions - Invites
  loadGroupInvites: (groupId: string) => Promise<void>;
  createInvite: (groupId: string, params?: { max_uses?: number; expires_in_hours?: number }) => Promise<GroupInvite>;
  revokeInvite: (groupId: string, inviteId: string) => Promise<void>;

  // Actions - Invite resolution
  resolveInvite: (code: string) => Promise<void>;
  acceptInvite: (code: string) => Promise<void>;

  // Actions - Notifications
  isNotificationsMuted: boolean;
  muteGroupNotifications: (groupId: string) => Promise<void>;
  unmuteGroupNotifications: (groupId: string) => Promise<void>;

  // Actions - Share to group
  postBlahblahToGroup: (groupId: string, blahblahId: string) => Promise<void>;
  loadMyGroupsForShare: () => Promise<Group[]>;

  // Reset
  resetDetail: () => void;
}

export const useGroupStore = create<GroupState>((set, get) => ({
  // List view
  activeTab: 'myGroups',
  myGroups: [],
  browseGroups: [],
  invites: [],
  searchQuery: '',
  searchSort: 'popular',
  isLoading: false,
  isLoadingMore: false,
  hasMore: true,
  nextCursor: null,
  error: null,

  // Detail view
  currentGroup: null,
  posts: [],
  pinnedPosts: [],
  postsNextCursor: null,
  postsHasMore: true,
  isLoadingDetail: false,
  isLoadingPosts: false,
  isLoadingMorePosts: false,

  // Members
  members: [],
  membersNextCursor: null,
  membersHasMore: true,
  isLoadingMembers: false,
  isLoadingMoreMembers: false,

  // Invites
  groupInvites: [],
  isLoadingInvites: false,

  // Notifications
  isNotificationsMuted: false,

  // Invite resolution
  inviteInfo: null,

  // ==================== LIST ACTIONS ====================

  setActiveTab: (tab) => {
    set({ activeTab: tab });
    const { myGroups, browseGroups, invites } = get();
    if (tab === 'myGroups' && myGroups.length === 0) get().loadMyGroups();
    if (tab === 'browse' && browseGroups.length === 0) get().loadBrowseGroups();
    if (tab === 'invites' && invites.length === 0) get().loadMyInvites();
  },

  loadMyGroups: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await groupsApi.getMyGroups();
      set({
        myGroups: response.items,
        nextCursor: response.nextCursor,
        hasMore: response.hasMore,
        isLoading: false,
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load groups', isLoading: false });
    }
  },

  loadBrowseGroups: async (query, sort) => {
    const q = query ?? get().searchQuery;
    const s = sort ?? get().searchSort;
    set({ isLoading: true, error: null, searchQuery: q, searchSort: s });
    try {
      const response = await groupsApi.browseGroups(s, q || undefined);
      set({
        browseGroups: response.items,
        nextCursor: response.nextCursor,
        hasMore: response.hasMore,
        isLoading: false,
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to browse groups', isLoading: false });
    }
  },

  loadMyInvites: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await groupsApi.getMyInvites();
      set({
        invites: response.items,
        nextCursor: response.nextCursor,
        hasMore: response.hasMore,
        isLoading: false,
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load invites', isLoading: false });
    }
  },

  loadMore: async () => {
    const { activeTab, nextCursor, hasMore, isLoadingMore } = get();
    if (!hasMore || isLoadingMore || !nextCursor) return;

    set({ isLoadingMore: true });
    try {
      let response;
      if (activeTab === 'myGroups') {
        response = await groupsApi.getMyGroups(nextCursor);
        set((s) => ({ myGroups: [...s.myGroups, ...response!.items] }));
      } else if (activeTab === 'browse') {
        const { searchSort, searchQuery } = get();
        response = await groupsApi.browseGroups(searchSort, searchQuery || undefined, nextCursor);
        set((s) => ({ browseGroups: [...s.browseGroups, ...response!.items] }));
      } else {
        response = await groupsApi.getMyInvites(nextCursor);
        set((s) => ({ invites: [...s.invites, ...response!.items] }));
      }
      set({
        nextCursor: response.nextCursor,
        hasMore: response.hasMore,
        isLoadingMore: false,
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load more', isLoadingMore: false });
    }
  },

  setSearchQuery: (query) => {
    set({ searchQuery: query });
    get().loadBrowseGroups(query);
  },

  setSearchSort: (sort) => {
    set({ searchSort: sort });
    get().loadBrowseGroups(undefined, sort);
  },

  // ==================== CRUD ACTIONS ====================

  createGroup: async (params) => {
    const group = await groupsApi.createGroup(params);
    set((s) => ({ myGroups: [group, ...s.myGroups] }));
    return group;
  },

  updateGroup: async (id, params) => {
    const group = await groupsApi.updateGroup(id, params);
    set((s) => ({
      currentGroup: s.currentGroup?.id === id ? group : s.currentGroup,
      myGroups: s.myGroups.map((g) => (g.id === id ? group : g)),
    }));
    return group;
  },

  deleteGroup: async (id) => {
    await groupsApi.deleteGroup(id);
    set((s) => ({
      myGroups: s.myGroups.filter((g) => g.id !== id),
      browseGroups: s.browseGroups.filter((g) => g.id !== id),
      currentGroup: s.currentGroup?.id === id ? null : s.currentGroup,
    }));
  },

  // ==================== DETAIL ACTIONS ====================

  loadGroupDetail: async (id) => {
    set({ isLoadingDetail: true, error: null });
    try {
      const group = await groupsApi.getGroup(id);
      set({ currentGroup: group, isLoadingDetail: false });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load group', isLoadingDetail: false });
    }
  },

  loadGroupFeed: async (groupId) => {
    set({ isLoadingPosts: true });
    try {
      const response = await groupsApi.getGroupFeed(groupId);
      const pinned = response.items.filter((p) => p.isPinned);
      const regular = response.items.filter((p) => !p.isPinned);
      set({
        posts: regular,
        pinnedPosts: pinned,
        postsNextCursor: response.nextCursor,
        postsHasMore: response.hasMore,
        isLoadingPosts: false,
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load feed', isLoadingPosts: false });
    }
  },

  loadMorePosts: async (groupId) => {
    const { postsNextCursor, postsHasMore, isLoadingMorePosts } = get();
    if (!postsHasMore || isLoadingMorePosts || !postsNextCursor) return;

    set({ isLoadingMorePosts: true });
    try {
      const response = await groupsApi.getGroupFeed(groupId, postsNextCursor);
      set((s) => ({
        posts: [...s.posts, ...response.items.filter((p) => !p.isPinned)],
        postsNextCursor: response.nextCursor,
        postsHasMore: response.hasMore,
        isLoadingMorePosts: false,
      }));
    } catch {
      set({ isLoadingMorePosts: false });
    }
  },

  joinGroup: async (id) => {
    try {
      await groupsApi.joinGroup(id);
      // Reload to get updated membership status
      get().loadGroupDetail(id);
    } catch (err: any) {
      const message = err?.response?.data?.error?.message || err?.message || 'Failed to join group';
      throw new Error(message);
    }
  },

  leaveGroup: async (id) => {
    await groupsApi.leaveGroup(id);
    set((s) => ({
      currentGroup: s.currentGroup ? { ...s.currentGroup, myRole: null, myStatus: null } : null,
      myGroups: s.myGroups.filter((g) => g.id !== id),
    }));
  },

  deletePost: async (groupId, postId) => {
    set((s) => ({
      posts: s.posts.filter((p) => p.id !== postId),
      pinnedPosts: s.pinnedPosts.filter((p) => p.id !== postId),
    }));
    try {
      await groupsApi.deleteGroupPost(groupId, postId);
    } catch {
      // Reload on error
      get().loadGroupFeed(groupId);
    }
  },

  pinPost: async (groupId, postId) => {
    await groupsApi.pinPost(groupId, postId);
    get().loadGroupFeed(groupId);
  },

  unpinPost: async (groupId, postId) => {
    await groupsApi.unpinPost(groupId, postId);
    get().loadGroupFeed(groupId);
  },

  // ==================== MEMBERS ACTIONS ====================

  loadMembers: async (groupId) => {
    set({ isLoadingMembers: true });
    try {
      const response = await groupsApi.getGroupMembers(groupId);
      set({
        members: response.items,
        membersNextCursor: response.nextCursor,
        membersHasMore: response.hasMore,
        isLoadingMembers: false,
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load members', isLoadingMembers: false });
    }
  },

  loadMoreMembers: async (groupId) => {
    const { membersNextCursor, membersHasMore, isLoadingMoreMembers } = get();
    if (!membersHasMore || isLoadingMoreMembers || !membersNextCursor) return;

    set({ isLoadingMoreMembers: true });
    try {
      const response = await groupsApi.getGroupMembers(groupId, membersNextCursor);
      set((s) => ({
        members: [...s.members, ...response.items],
        membersNextCursor: response.nextCursor,
        membersHasMore: response.hasMore,
        isLoadingMoreMembers: false,
      }));
    } catch {
      set({ isLoadingMoreMembers: false });
    }
  },

  approveMember: async (groupId, userId) => {
    await groupsApi.approveMember(groupId, userId);
    get().loadMembers(groupId);
  },

  rejectMember: async (groupId, userId) => {
    await groupsApi.rejectMember(groupId, userId);
    set((s) => ({
      members: s.members.filter((m) => m.userId !== userId),
    }));
  },

  removeMember: async (groupId, userId) => {
    await groupsApi.removeMember(groupId, userId);
    set((s) => ({
      members: s.members.filter((m) => m.userId !== userId),
    }));
  },

  banMember: async (groupId, userId) => {
    await groupsApi.banMember(groupId, userId);
    get().loadMembers(groupId);
  },

  unbanMember: async (groupId, userId) => {
    await groupsApi.unbanMember(groupId, userId);
    get().loadMembers(groupId);
  },

  muteMember: async (groupId, userId) => {
    await groupsApi.muteMember(groupId, userId);
    get().loadMembers(groupId);
  },

  unmuteMember: async (groupId, userId) => {
    await groupsApi.unmuteMember(groupId, userId);
    get().loadMembers(groupId);
  },

  updateMemberRole: async (groupId, userId, role) => {
    await groupsApi.updateMemberRole(groupId, userId, role);
    get().loadMembers(groupId);
  },

  transferOwnership: async (groupId, userId) => {
    await groupsApi.transferOwnership(groupId, userId);
    get().loadGroupDetail(groupId);
    get().loadMembers(groupId);
  },

  // ==================== INVITES ACTIONS ====================

  loadGroupInvites: async (groupId) => {
    set({ isLoadingInvites: true });
    try {
      const invitesList = await groupsApi.getInvites(groupId);
      set({ groupInvites: invitesList, isLoadingInvites: false });
    } catch {
      set({ isLoadingInvites: false });
    }
  },

  createInvite: async (groupId, params) => {
    const invite = await groupsApi.createInvite(groupId, params);
    set((s) => ({ groupInvites: [invite, ...s.groupInvites] }));
    return invite;
  },

  revokeInvite: async (groupId, inviteId) => {
    await groupsApi.revokeInvite(groupId, inviteId);
    set((s) => ({
      groupInvites: s.groupInvites.filter((i) => i.id !== inviteId),
    }));
  },

  // ==================== INVITE RESOLUTION ====================

  resolveInvite: async (code) => {
    set({ isLoading: true, error: null, inviteInfo: null });
    try {
      const info = await groupsApi.getInviteInfo(code);
      set({ inviteInfo: info, isLoading: false });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Invalid invite', isLoading: false });
    }
  },

  acceptInvite: async (code) => {
    try {
      await groupsApi.acceptInvite(code);
    } catch (err: any) {
      const message = err?.response?.data?.error?.message || err?.message || 'Failed to accept invite';
      throw new Error(message);
    }
  },

  // ==================== NOTIFICATIONS ====================

  muteGroupNotifications: async (groupId) => {
    await groupsApi.muteGroupNotifications(groupId);
    set({ isNotificationsMuted: true });
  },

  unmuteGroupNotifications: async (groupId) => {
    await groupsApi.unmuteGroupNotifications(groupId);
    set({ isNotificationsMuted: false });
  },

  // ==================== SHARE TO GROUP ====================

  postBlahblahToGroup: async (groupId, blahblahId) => {
    await groupsApi.postToGroup(groupId, blahblahId);
    // If currently viewing this group, refresh the feed
    const { currentGroup } = get();
    if (currentGroup?.id === groupId) {
      get().loadGroupFeed(groupId);
    }
  },

  loadMyGroupsForShare: async () => {
    const response = await groupsApi.getMyGroups();
    return response.items;
  },

  // ==================== RESET ====================

  resetDetail: () => {
    set({
      currentGroup: null,
      posts: [],
      pinnedPosts: [],
      postsNextCursor: null,
      postsHasMore: true,
      members: [],
      membersNextCursor: null,
      membersHasMore: true,
      groupInvites: [],
      isNotificationsMuted: false,
    });
  },
}));
